import { cn } from '@/utils/style'

export type ColorScheme = 'normal' | 'expired' | 'noPackage'

export interface BarSegment {
  ratio: number
  className: string
}

interface SegmentedProgressProps {
  value: number
  secondValue?: number
  className?: string
  colorScheme?: ColorScheme
  segments?: BarSegment[]
  remainderClassName?: string
}

const COLOR_SCHEMES = {
  normal: {
    segment1: 'bg-btn-tertiary',
    segment2: 'bg-btn-secondary',
    segment3: 'bg-active',
  },
  expired: {
    segment1: 'bg-bar-tertiary',
    segment2: 'bg-bar-tertiary/60',
    segment3: 'bg-tertiary',
  },
  noPackage: {
    segment1: 'bg-btn-tertiary',
    segment2: 'bg-btn-secondary',
    segment3: 'bg-neutral-200',
  },
}

export function SegmentedProgressBar({
  value,
  secondValue = 0,
  className,
  colorScheme = 'normal',
  segments,
  remainderClassName = 'bg-neutral-200',
}: SegmentedProgressProps) {
  if (segments) {
    const visibleSegments = segments.filter((s) => s.ratio > 0)
    const usedRatio = Math.min(
      1,
      visibleSegments.reduce((sum, s) => sum + s.ratio, 0),
    )
    return (
      <div className={cn('w-full h-6 rounded overflow-hidden gap-1 flex', className)}>
        {visibleSegments.map((segment, index) => (
          <div
            key={index}
            className={cn('h-full rounded', segment.className)}
            style={{ width: `${Math.min(segment.ratio, 1) * 100}%` }}
          />
        ))}
        {usedRatio < 1 && (
          <div
            className={cn('h-full rounded', remainderClassName)}
            style={{ width: `${(1 - usedRatio) * 100}%` }}
          />
        )}
      </div>
    )
  }

  const total = 1
  const segment1Width = (value / total) * 100
  const segment2Width = secondValue > value ? ((secondValue - value) / total) * 100 : 0
  const segment3Width = 100 - segment1Width - segment2Width

  const colors = COLOR_SCHEMES[colorScheme]

  return (
    <div className={cn('w-full h-6 rounded overflow-hidden gap-1 flex', className)}>
      {segment1Width > 0 && (
        <div
          className={cn('h-full rounded', colors.segment1)}
          style={{ width: `${segment1Width}%` }}
        />
      )}
      {segment2Width > 0 && (
        <div
          className={cn('h-full rounded', colors.segment2)}
          style={{ width: `${segment2Width}%` }}
        />
      )}
      {segment3Width > 0 && (
        <div
          className={cn('h-full rounded', colors.segment3)}
          style={{ width: `${segment3Width}%` }}
        />
      )}
    </div>
  )
}
